import React from 'react';
import {
  Paper,
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  Tooltip,
} from '@mui/material';
import { Schedule as ScheduleIcon, InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';
import InlineLoader from './common/InlineLoader';

// Componente header colonna con icona "i" alla destra senza spostare il testo
const HeaderWithInfo: React.FC<{ label: string; tooltip: string }> = ({ label, tooltip }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%', gap: 0.3 }}>
    <span>{label}</span>
    <Tooltip title={tooltip} arrow placement="top">
      <InfoOutlinedIcon
        sx={{
          fontSize: '0.7rem',
          color: 'text.disabled',
          cursor: 'help',
          flexShrink: 0,
          '&:hover': { color: 'primary.main' },
        }}
      />
    </Tooltip>
  </Box>
);

// Colore tema per Generazione Orari (tenuto uguale al parent)
const PAGE_COLOR = '#607D8B';

// Helper function per convertire ore decimali in formato hh:mm
const formatOreAsHHMM = (ore: number): string => {
  const segno = ore < 0 ? '-' : '';
  const totalMinutes = Math.round(Math.abs(ore) * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${segno}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

// Tipi locali per i dati delle statistiche (aderenti al shape usato nel parent)
interface DipendenteStatistiche {
  nomeDipendente: string;
  cognomeDipendente: string;
  oreMensiliContratto: number;
  oreMensiliAssegnate: number;
  attuale?: number;
  tipoContratto?: string;
  lunedi?: number;
  martedi?: number;
  mercoledi?: number;
  giovedi?: number;
  venerdi?: number;
  sabato?: number;
  domenica?: number;
  oreSettimanali?: number;
}

interface DipendenteStatisticheTurni {
  nomeDipendente: string;
  cognomeDipendente: string;
  mattine: number;
  pomeriggi: number;
  intermezzi: number;
  riposiSettimanali: number;
  riposiSabati: number;
  riposiDomeniche: number;
}

type Props = {
  meseLabel: string;
  anno: number;
  loading: boolean;
  hasStatistiche: boolean;
  statisticheSorted: DipendenteStatistiche[];
  statisticheTurniSorted: DipendenteStatisticheTurni[];
};

const GenerazioneOrarioStats: React.FC<Props> = ({
  meseLabel,
  anno,
  loading,
  hasStatistiche,
  statisticheSorted,
  statisticheTurniSorted,
}) => {
  const hasStatisticheTurni = statisticheTurniSorted && statisticheTurniSorted.length > 0;

  return (
    <Paper sx={{ mt: 3, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <ScheduleIcon sx={{ mr: 1, color: PAGE_COLOR }} />
        <Typography variant="h5" component="h2">
          Statistiche Dipendenti - {meseLabel} {anno}
        </Typography>
      </Box>

      {loading ? (
        <InlineLoader rows={4} />
      ) : (
        hasStatistiche ? (
          <Box>
            {/* Prima tabella: Ore e Stato */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Ore e Assegnazioni</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Dipendente</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Tipo Contratto</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Lun</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Mar</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Mer</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Gio</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Ven</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Sab</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Dom</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Sett</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', position: 'relative', pr: 0.05 }}>
                        <HeaderWithInfo label="Ore Contratto" tooltip="Ore totali previste dal contratto del dipendente per il mese, calcolate in base ai giorni lavorativi settimanali." />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', position: 'relative', pr: 0.05 }}>
                        <HeaderWithInfo label="Ore Pianificate" tooltip="Ore già assegnate in orario per il mese (somma delle ore dei turni pianificati)." />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', position: 'relative', pr: 0.05 }}>
                        <HeaderWithInfo label="Attuale" tooltip="Stima delle ore effettive: ore consuntivate dove disponibili + ore pianificate per i giorni non ancora consuntivati + ore contrattuali per assenze speciali (es. maternità, legge 104)." />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', position: 'relative', pr: 0.05 }}>
                        <HeaderWithInfo label="Differenza" tooltip="Differenza tra Attuale e Ore Contratto. Positivo = ore in eccesso, negativo = ore mancanti." />
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statisticheSorted.map((stat, index) => {
                      const differenzaRaw = (stat.attuale ?? stat.oreMensiliAssegnate) - stat.oreMensiliContratto;
                      const differenza = Math.round(differenzaRaw * 60) / 60;
                      const percentuale = stat.oreMensiliContratto > 0
                        ? (stat.oreMensiliAssegnate / stat.oreMensiliContratto) * 100
                        : 0;
                      const isCompleto = percentuale >= 95;
                      const isEccessivo = percentuale > 110;
                      return (
                        <TableRow
                          key={index}
                          sx={{
                            '&:nth-of-type(odd)': { bgcolor: 'action.hover' },
                            bgcolor: isEccessivo ? 'error.50' : isCompleto ? 'success.50' : 'warning.50'
                          }}
                        >
                          <TableCell sx={{ fontWeight: 'medium' }}>
                            {stat.nomeDipendente} {stat.cognomeDipendente}
                          </TableCell>
                          <TableCell align="center">{stat.tipoContratto ?? ''}</TableCell>
                          <TableCell align="center">{stat.lunedi ?? 0}</TableCell>
                          <TableCell align="center">{stat.martedi ?? 0}</TableCell>
                          <TableCell align="center">{stat.mercoledi ?? 0}</TableCell>
                          <TableCell align="center">{stat.giovedi ?? 0}</TableCell>
                          <TableCell align="center">{stat.venerdi ?? 0}</TableCell>
                          <TableCell align="center">{stat.sabato ?? 0}</TableCell>
                          <TableCell align="center">{stat.domenica ?? 0}</TableCell>
                          <TableCell align="center">{stat.oreSettimanali ?? 0}</TableCell>
                          <TableCell align="right">
                            {formatOreAsHHMM(stat.oreMensiliContratto)}
                          </TableCell>
                          <TableCell align="right">
                            {formatOreAsHHMM(stat.oreMensiliAssegnate)}
                          </TableCell>
                          <TableCell align="right">
                            {stat.attuale != null ? formatOreAsHHMM(stat.attuale) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {differenza < 0 ? (
                              <Box component="span" sx={{
                                bgcolor: '#d32f2f',
                                color: 'white',
                                fontWeight: 'bold',
                                borderRadius: '16px',
                                px: 1,
                                py: 0.25,
                                display: 'inline-block',
                                fontSize: '0.8rem'
                              }}>
                                {formatOreAsHHMM(differenza)}
                              </Box>
                            ) : (
                              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 'medium' }}>
                                {differenza > 0 ? '+' : ''}{formatOreAsHHMM(differenza)}
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Seconda tabella: Statistiche Turni */}
            {hasStatisticheTurni && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Distribuzione Turni e Riposi</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Dipendente</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Mattine</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Pomeriggi</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Intermezzi</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Riposi Settimanali</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Riposi Sabati</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Riposi Domeniche</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {statisticheTurniSorted.map((stat, index) => (
                        <TableRow
                          key={index}
                          sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}
                        >
                          <TableCell sx={{ fontWeight: 'medium' }}>
                            {stat.nomeDipendente} {stat.cognomeDipendente}
                          </TableCell>
                          <TableCell align="center">
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'info.main',
                                fontWeight: 'medium'
                              }}
                            >
                              {stat.mattine}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'warning.main',
                                fontWeight: 'medium'
                              }}
                            >
                              {stat.pomeriggi}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'success.main',
                                fontWeight: 'medium'
                              }}
                            >
                              {stat.intermezzi}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.secondary',
                                fontWeight: 'medium'
                              }}
                            >
                              {stat.riposiSettimanali}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'grey.600',
                                fontWeight: 'medium'
                              }}
                            >
                              {stat.riposiSabati}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'grey.600',
                                fontWeight: 'medium'
                              }}
                            >
                              {stat.riposiDomeniche}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        ) : (
          <Alert severity="info">
            Nessuna statistica disponibile. Genera prima gli orari per questo periodo.
          </Alert>
        )
      )}
    </Paper>
  );
};

export default GenerazioneOrarioStats;
